<template>
  <div class="staff-sales">
    <h1 class="text-h5 mb-2">Staff product sales</h1>
    <p class="text-body-2">Compare how many of your chosen products each staff member sold.</p>

    <v-card outlined class="pa-4 mb-4">
      <h2 class="text-h6 mb-3">1. Choose dates</h2>
      <v-row>
        <v-col cols="12" sm="6"><v-text-field v-model="startDate" type="date" label="Start date" outlined dense hide-details :disabled="isLoading" /></v-col>
        <v-col cols="12" sm="6"><v-text-field v-model="endDate" type="date" label="End date" outlined dense hide-details :disabled="isLoading" /></v-col>
      </v-row>
      <p v-if="!validDates" class="error--text mt-3" role="alert">Choose valid dates, with the end date on or after the start date.</p>
    </v-card>

    <v-card outlined class="pa-4 mb-4">
      <h2 class="text-h6 mb-2">2. Build your product bank</h2>
      <p class="text-body-2">Search a product such as garlic bread, or a category such as desserts. Your selections stay when you search again.</p>
      <v-alert v-if="productsError" type="error" text>
        {{ productsError }} <v-btn text small @click="getProducts">Retry</v-btn>
      </v-alert>
      <v-text-field v-model="search" label="Search products or categories" prepend-inner-icon="mdi-magnify" clearable outlined dense hide-details class="mb-3" />
      <div class="selection-actions mb-3">
        <v-btn small outlined :disabled="!filteredProducts.length || isLoading" @click="selectMatching">Select all matching ({{ filteredProducts.length }})</v-btn>
        <v-btn small text :disabled="!selectedProducts.length || isLoading" @click="selectedProducts = []">Clear selection</v-btn>
        <span role="status">{{ selectedProducts.length }} products selected</span>
      </div>
      <v-data-table
        v-model="selectedProducts" :headers="productHeaders" :items="filteredProducts"
        :loading="productsLoading" :items-per-page="10" item-key="id" show-select
        :mobile-breakpoint="600"
        no-data-text="No matching products. Try another product or category."
      >
        <template v-slot:[`header.data-table-select`]>
          <input type="checkbox" :checked="allMatchingSelected" :indeterminate.prop="someMatchingSelected && !allMatchingSelected"
            :disabled="!filteredProducts.length || isLoading" aria-label="Select all matching products"
            @change="toggleMatching($event.target.checked)" />
        </template>
        <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
          <input type="checkbox" :checked="isSelected" :disabled="isLoading" :aria-label="'Select ' + item.name" @change="select($event.target.checked)" />
        </template>
        <template v-slot:[`item.category.name`]="{ item }">{{ item.category ? item.category.name : 'Uncategorised' }}</template>
      </v-data-table>
      <details v-if="selectedProducts.length" class="mt-3">
        <summary>Selected products ({{ selectedProducts.length }})</summary>
        <div class="selected-bank mt-2">
          <v-chip v-for="product in selectedProducts" :key="product.id" close :disabled="isLoading" @click:close="removeProduct(product.id)">{{ product.name }}</v-chip>
        </div>
      </details>
    </v-card>

    <v-btn color="primary" large :block="$vuetify.breakpoint.smAndDown" :loading="isLoading"
      :disabled="!validDates || !selectedProducts.length || productsLoading" @click="load">Generate report</v-btn>
    <v-alert v-if="error" type="error" text class="mt-4" role="alert">{{ error }}</v-alert>

    <section v-if="generated" class="mt-6" aria-label="Staff sales results" aria-live="polite">
      <h2 class="text-h6">3. Staff comparison</h2>
      <p class="text-body-2">{{ generated.startDate }} to {{ generated.endDate }} &middot; {{ generated.productCount }} selected products</p>
      <v-alert v-if="filtersChanged" type="info" text>Selections or dates have changed. Generate the report again to update these results.</v-alert>
      <p class="text-caption">Attributed to the staff member recorded on each order. Quantities and revenue are before order-level refunds; deleted orders are excluded. Sales share measures volume, without adjusting for shifts worked.</p>
      <v-row>
        <v-col v-for="stat in summary" :key="stat.label" cols="12" sm="4">
          <v-card outlined class="pa-4"><div class="text-overline">{{ stat.label }}</div><div class="text-h4">{{ stat.value }}</div></v-card>
        </v-col>
      </v-row>
      <v-alert v-if="!data.length" type="info" text class="mt-4">No staff orders found in this date range.</v-alert>
      <template v-else>
        <v-card outlined class="pa-4 my-4">
          <h3 class="text-subtitle-1 mb-3">Orders containing selected products</h3>
          <p class="text-body-2">Matching orders / all orders taken by that staff member. Each order counts once, even if it contains several selected products.</p>
          <div v-for="staff in rankedStaff" :key="staff.staffId" class="mb-5">
            <div class="comparison-label"><span>{{ staff.staffName }}</span><strong>{{ staff.matchingOrders }} of {{ staff.totalOrders }} orders &middot; {{ percentage(staff.orderPercentage) }}%</strong></div>
            <v-progress-linear :value="staff.orderPercentage" height="12" rounded color="primary" :aria-label="staff.staffName + ' percentage of orders containing selected products'" />
            <div class="text-caption mt-1">{{ staff.totalQuantitySold }} selected units sold &middot; {{ percentage(staff.quantityPercentage) }}% of all selected units</div>
            <details class="mt-2">
              <summary>Product breakdown</summary>
              <p class="text-caption mt-2">Each product's orders / {{ staff.matchingOrders }} matching orders. An order can contain several products, so percentages may total over 100%.</p>
              <div v-for="product in productBreakdown(staff)" :key="product.productId" class="mb-3 pl-3">
                <div class="comparison-label"><span>{{ product.productName }}</span><span>{{ product.orders }} orders &middot; {{ percentage(product.matchingOrderPercentage) }}%</span></div>
                <v-progress-linear :value="product.matchingOrderPercentage" height="6" color="primary" :aria-label="product.productName + ' percentage of matching orders'" />
              </div>
            </details>
          </div>
        </v-card>
        <v-data-table :headers="headers" :items="data" :items-per-page="10" item-key="staffId"
          sort-by="orderPercentage" :sort-desc="true" show-expand single-expand :mobile-breakpoint="600">
          <template v-slot:[`item.orderPercentage`]="{ item }">{{ percentage(item.orderPercentage) }}%</template>
          <template v-slot:[`item.quantityPercentage`]="{ item }">{{ percentage(item.quantityPercentage) }}%</template>
          <template v-slot:[`item.totalRevenue`]="{ item }">{{ money(item.totalRevenue) }}</template>
          <template v-slot:expanded-item="{ item, headers: columns }">
            <td :colspan="columns.length" class="pa-4">
              <h3 class="text-subtitle-1 mb-2">{{ item.staffName }} &mdash; sale details</h3>
              <v-data-table :headers="saleHeaders" :items="item.sales" :items-per-page="10" :mobile-breakpoint="600">
                <template v-slot:[`item.saleDate`]="{ item: sale }">{{ formatDate(sale.saleDate) }}</template>
                <template v-slot:[`item.price`]="{ item: sale }">{{ money(sale.price - sale.discount) }}</template>
              </v-data-table>
            </td>
          </template>
        </v-data-table>
      </template>
    </section>
  </div>
</template>

<script>
import { reportService, productService } from '@/services';
import moment from 'moment';

export default {
  name: 'StaffProductSales',
  data: () => ({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    products: [], selectedProducts: [], search: '', productsLoading: false,
    productsError: '', isLoading: false, error: '', data: [], generated: null,
    productHeaders: [
      { text: 'Product', value: 'name' },
      { text: 'Category', value: 'category.name' }
    ],
    headers: [
      { text: 'Staff member', value: 'staffName' },
      { text: 'All orders', value: 'totalOrders', align: 'right' },
      { text: 'Matching orders', value: 'matchingOrders', align: 'right' },
      { text: 'Orders with selected products', value: 'orderPercentage', align: 'right' },
      { text: 'Units sold', value: 'totalQuantitySold', align: 'right' },
      { text: 'Share of units', value: 'quantityPercentage', align: 'right' },
      { text: 'Revenue', value: 'totalRevenue', align: 'right' },
      { text: '', value: 'data-table-expand' }
    ],
    saleHeaders: [
      { text: 'Date', value: 'saleDate' },
      { text: 'Order ref', value: 'orderRef' },
      { text: 'Product', value: 'productName' },
      { text: 'Quantity', value: 'quantity', align: 'right' },
      { text: 'Unit price after discount', value: 'price', align: 'right' }
    ]
  }),
  computed: {
    rankedStaff() { return this.data.slice().sort((a, b) => b.orderPercentage - a.orderPercentage || b.matchingOrders - a.matchingOrders); },
    validDates() {
      return moment(this.startDate, 'YYYY-MM-DD', true).isValid()
        && moment(this.endDate, 'YYYY-MM-DD', true).isValid() && this.startDate <= this.endDate;
    },
    filteredProducts() {
      const query = (this.search || '').trim().toLowerCase();
      return this.products.filter(p => (p.name || '').toLowerCase().includes(query)
        || (p.category?.name || '').toLowerCase().includes(query));
    },
    allMatchingSelected() {
      const ids = new Set(this.selectedProducts.map(p => p.id));
      return this.filteredProducts.length > 0 && this.filteredProducts.every(p => ids.has(p.id));
    },
    someMatchingSelected() {
      const ids = new Set(this.selectedProducts.map(p => p.id));
      return this.filteredProducts.some(p => ids.has(p.id));
    },
    filterKey() {
      return JSON.stringify([this.startDate, this.endDate, this.selectedProducts.map(p => p.id).sort()]);
    },
    filtersChanged() { return this.generated && this.generated.key !== this.filterKey; },
    summary() {
      return [
        { label: 'Total units sold', value: this.data.reduce((sum, s) => sum + s.totalQuantitySold, 0) },
        { label: 'Staff with orders', value: this.data.length },
        { label: 'Total revenue', value: this.money(this.data.reduce((sum, s) => sum + s.totalRevenue, 0)) }
      ];
    }
  },
  created() { this.getProducts(); },
  methods: {
    productBreakdown(staff) {
      const sold = new Map((staff.products || []).map(p => [p.productId, p]));
      return this.generated.products.map(p => sold.get(p.id) || {
        productId: p.id, productName: p.name, orders: 0, matchingOrderPercentage: 0
      }).sort((a, b) => b.orders - a.orders);
    },
    async getProducts() {
      this.productsLoading = true;
      this.productsError = '';
      try {
        const products = await productService.get();
        if (!Array.isArray(products)) throw new Error();
        this.products = products;
      } catch (_) {
        this.productsError = 'Unable to load products. Please try again.';
      } finally { this.productsLoading = false; }
    },
    selectMatching() {
      const selected = new Map(this.selectedProducts.map(p => [p.id, p]));
      this.filteredProducts.forEach(p => selected.set(p.id, p));
      this.selectedProducts = Array.from(selected.values());
    },
    toggleMatching(value) {
      if (value) return this.selectMatching();
      const matching = new Set(this.filteredProducts.map(p => p.id));
      this.selectedProducts = this.selectedProducts.filter(p => !matching.has(p.id));
    },
    removeProduct(id) { this.selectedProducts = this.selectedProducts.filter(p => p.id !== id); },
    async load() {
      if (this.isLoading || !this.validDates || !this.selectedProducts.length) return;
      const snapshot = { startDate: this.startDate, endDate: this.endDate, productCount: this.selectedProducts.length,
        products: this.selectedProducts.map(p => ({ id: p.id, name: p.name })), key: this.filterKey };
      const ids = this.selectedProducts.map(p => p.id);
      this.isLoading = true;
      this.error = '';
      try {
        const result = await reportService.getStaffProductSalesReport(snapshot.startDate, snapshot.endDate, ids);
        if (!Array.isArray(result)) throw new Error();
        this.data = result;
        this.generated = snapshot;
      } catch (_) {
        this.error = 'Unable to generate the report. Please try again. Any results below are from the previous report.';
      } finally { this.isLoading = false; }
    },
    percentage(value) { return Number(value || 0).toFixed(1); },
    money(value) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0); },
    formatDate(value) { return moment(value).format('DD/MM/YYYY HH:mm'); }
  }
};
</script>

<style scoped>
.staff-sales { max-width: 1200px; margin: 0 auto; }
.selection-actions, .selected-bank { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.selected-bank { max-height: 220px; overflow-y: auto; }
.comparison-label { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 6px; flex-wrap: wrap; }
summary { cursor: pointer; }
input[type="checkbox"] { width: 18px; height: 18px; vertical-align: middle; }
</style>
